/**
 * MilestoneVideoModal — Plein écran modale qui joue une courte vidéo Sora 2
 * (4 s) à chaque grande étape du jeu. Mounté dans Layout.jsx ; surveille
 * `state.lastMilestoneEvent` et l'affiche tant que le joueur n'a pas
 * cliqué « Continuer ». Fallback gracieux si l'asset MP4 est absent
 * (animation badge + emoji).
 *
 * 08/02/2026 — Refonte phases & narratifs (Q2 user prod).
 *
 * Mapping milestone → MP4 :
 *   - phase-up-2          → milestone-clips/vehicle-tier-up.mp4
 *                           (le déclencheur de P2 = agrandissement = montée
 *                            en gamme véhicule, le clip "delivery truck"
 *                            est sémantiquement le meilleur)
 *   - phase-up-3          → milestone-clips/depot-open.mp4
 *   - phase-up-4          → milestone-clips/competitor-buyout.mp4
 *                           (transition "international" symbolisée par le
 *                            "closing" — signature finale)
 *   - vehicle-tier-up     → milestone-clips/vehicle-tier-up.mp4
 *   - depot-open          → milestone-clips/depot-open.mp4
 *   - competitor-buyout   → milestone-clips/competitor-buyout.mp4
 *   - ao-national-win     → milestone-clips/ao-national-win.mp4
 *   - office-upgrade      → milestone-clips/office-upgrade.mp4
 *   - game-over           → milestone-clips/game-over.mp4
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, X, Sparkles, Crown, Truck, Building2, HandCoins, Skull } from 'lucide-react';
import useGameStore from '../store/gameStore';

const CLIP_BY_KIND = {
  // 08/02/2026 v3 — clips dédiés pour les phase ups (générés via Sora 2,
  // ~2.2-2.7 MB chacun).
  'phase-up-2': '/milestone-clips/phase-up-2.mp4',
  'phase-up-3': '/milestone-clips/phase-up-3.mp4',
  'phase-up-4': '/milestone-clips/phase-up-4.mp4',
  'vehicle-tier-up': '/milestone-clips/vehicle-tier-up.mp4',
  'depot-open': '/milestone-clips/depot-open.mp4',
  'competitor-buyout': '/milestone-clips/competitor-buyout.mp4',
  'ao-national-win': '/milestone-clips/ao-national-win.mp4',
  'office-upgrade': '/milestone-clips/office-upgrade.mp4',
  'game-over': '/milestone-clips/game-over.mp4',
  // 25/07/2026 — convoi exceptionnel livré (demande user : effet média +
  // vidéo). Clip dédié si présent, sinon fallback camion (vehicle-tier-up).
  'convoy-exceptional': '/milestone-clips/convoy-exceptional.mp4',
  // 30/06 — Dynamique groupe SCD (3 magasins) : clip dédié (diaporama cinématique).
  'groupe-unlocked': '/milestone-clips/groupe-unlocked.mp4',
};

// 08/02/2026 v3 — fallback chain conservé en filet de sécurité si un
// asset MP4 est absent (CDN miss). MilestoneVideoModal essaie le clip
// dédié d'abord, puis le fallback, puis le TrophyFallback icon-based.
const FALLBACK_CLIP = {
  'phase-up-2': '/milestone-clips/vehicle-tier-up.mp4',
  'phase-up-3': '/milestone-clips/depot-open.mp4',
  'phase-up-4': '/milestone-clips/competitor-buyout.mp4',
  'convoy-exceptional': '/milestone-clips/vehicle-tier-up.mp4',
};

const LABEL_BY_KIND = {
  'phase-up-2': { title: 'Cap régional franchi', sub: 'Phase 2 — Régional débloquée', icon: Sparkles, color: 'sky' },
  'phase-up-3': { title: 'Cap national franchi', sub: 'Phase 3 — National débloquée', icon: Crown, color: 'amber' },
  'phase-up-4': { title: 'Cap international franchi', sub: 'Phase 4 — International débloquée', icon: Crown, color: 'emerald' },
  'vehicle-tier-up': { title: 'Premier véhicule Tier 2+', sub: 'Votre flotte monte en gamme', icon: Truck, color: 'sky' },
  'depot-open': { title: 'Nouveau dépôt inauguré', sub: 'Votre maillage logistique s’étend', icon: Building2, color: 'amber' },
  'competitor-buyout': { title: 'Concurrent racheté', sub: 'Closing signé — intégration en cours', icon: HandCoins, color: 'amber' },
  'ao-national-win': { title: 'AO National remporté', sub: 'Contrat majeur attribué', icon: Trophy, color: 'amber' },
  'office-upgrade': { title: 'Bureaux modernisés', sub: 'Image corporate renforcée', icon: Sparkles, color: 'sky' },
  'game-over': { title: 'Liquidation prononcée', sub: 'L’aventure s’arrête ici', icon: Skull, color: 'slate' },
  'convoy-exceptional': { title: 'Convoi exceptionnel livré', sub: 'Logistique d’exception — vitrine médiatique', icon: Truck, color: 'emerald' },
  'groupe-unlocked': { title: 'Dynamique groupe enclenchée', sub: '3 magasins — investissez dans votre siège social et recrutez vos fonctions support', icon: Building2, color: 'amber' },
};

function getColorClasses(c) {
  switch (c) {
    case 'amber':
      return {
        border: 'border-amber-400/60',
        bg: 'bg-gradient-to-br from-amber-900/40 via-ink-900 to-amber-950/30',
        shadow: 'shadow-[0_0_60px_-10px_rgba(251,191,36,0.45)]',
        title: 'text-amber-300',
        subtle: 'text-amber-200/80',
        btn: 'bg-amber-600/30 border border-amber-500/50 text-amber-100 hover:bg-amber-600/40',
        ring: 'ring-amber-400/20',
        icon: 'text-amber-300',
      };
    case 'sky':
      return {
        border: 'border-sky-400/60',
        bg: 'bg-gradient-to-br from-sky-900/40 via-ink-900 to-sky-950/30',
        shadow: 'shadow-[0_0_50px_-10px_rgba(56,189,248,0.4)]',
        title: 'text-sky-300',
        subtle: 'text-sky-200/80',
        btn: 'bg-sky-600/30 border border-sky-500/50 text-sky-100 hover:bg-sky-600/40',
        ring: 'ring-sky-400/20',
        icon: 'text-sky-300',
      };
    case 'emerald':
      return {
        border: 'border-emerald-400/60',
        bg: 'bg-gradient-to-br from-emerald-900/40 via-ink-900 to-emerald-950/30',
        shadow: 'shadow-[0_0_60px_-10px_rgba(52,211,153,0.45)]',
        title: 'text-emerald-300',
        subtle: 'text-emerald-200/80',
        btn: 'bg-emerald-600/30 border border-emerald-500/50 text-emerald-100 hover:bg-emerald-600/40',
        ring: 'ring-emerald-400/20',
        icon: 'text-emerald-300',
      };
    default:
      return {
        border: 'border-slate-400/40',
        bg: 'bg-gradient-to-br from-slate-800/60 via-ink-900 to-slate-900/30',
        shadow: 'shadow-2xl',
        title: 'text-slate-300',
        subtle: 'text-slate-300/80',
        btn: 'bg-slate-700/40 border border-slate-500/40 text-slate-200 hover:bg-slate-700/50',
        ring: 'ring-slate-400/20',
        icon: 'text-slate-400',
      };
  }
}

export default function MilestoneVideoModal() {
  const evt = useGameStore(s => s.lastMilestoneEvent);
  const consume = useGameStore(s => s.consumeLastMilestoneEvent);
  const [open, setOpen] = useState(null);

  const close = useCallback(() => {
    setOpen(null);
    if (typeof consume === 'function') consume();
  }, [consume]);

  useEffect(() => {
    if (!evt) return;
    setOpen(evt);
  }, [evt]);

  if (!open) return (
    <AnimatePresence>{null}</AnimatePresence>
  );

  const meta = LABEL_BY_KIND[open.kind] || { title: 'Étape franchie', sub: '', icon: Sparkles, color: 'amber' };
  // 09/02/2026 — Q2 Lot I user prod : titre dynamique pour 'vehicle-tier-up'.
  // Le user a remonté que le titre affichait « Tier 2+ » alors qu'il avait
  // acheté un Tier 3. On extrait le tier réel depuis open.reason (format
  // « Brand Model (Tier N) ») et on remplace le titre.
  let dynamicTitle = meta.title;
  if (open.kind === 'vehicle-tier-up' && typeof open.reason === 'string') {
    const m = open.reason.match(/Tier (\d+)/i);
    if (m) {
      dynamicTitle = `Premier véhicule Tier ${m[1]}`;
    }
  }
  const cls = getColorClasses(meta.color);
  const clipSrc = CLIP_BY_KIND[open.kind] || null;
  const Icon = meta.icon;

  return (
    <AnimatePresence>
      <motion.div
        key="milestone-video-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
        onClick={close}
        data-testid="milestone-video-modal"
      >
        <motion.div
          initial={{ scale: 0.85, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 240, damping: 22 }}
          className={`relative w-full max-w-lg rounded-2xl border-2 p-5 ${cls.border} ${cls.bg} ${cls.shadow}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-2 right-2 p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
            data-testid="milestone-video-modal-close"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <Icon className={`w-5 h-5 ${cls.icon}`} />
            <h3 className={`text-xs uppercase tracking-widest font-mono font-bold ${cls.title}`}>
              {meta.sub}
            </h3>
          </div>

          <p className="text-lg font-display font-bold mb-3 text-white" data-testid="milestone-video-title">
            {dynamicTitle}
          </p>

          {clipSrc && (
            <ClipPlayer
              src={clipSrc}
              fallbackSrc={FALLBACK_CLIP[open.kind] || null}
              fallback={<TrophyFallback color={meta.color} Icon={Icon} />}
              ringClass={cls.ring}
            />
          )}

          {open.reason && (
            <p className={`mt-3 text-xs ${cls.subtle}`} data-testid="milestone-video-reason">
              {open.reason}
            </p>
          )}

          <button
            type="button"
            onClick={close}
            className={`mt-4 w-full py-2 rounded text-sm font-semibold transition-colors ${cls.btn}`}
            data-testid="milestone-video-modal-ack"
          >
            Continuer
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ClipPlayer({ src, fallbackSrc, fallback, ringClass }) {
  const [broken, setBroken] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  if (broken) return fallback;
  const currentSrc = useFallback && fallbackSrc ? fallbackSrc : src;
  return (
    <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black aspect-video">
      <video
        key={currentSrc}
        src={currentSrc}
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-full object-cover"
        onError={() => {
          if (fallbackSrc && !useFallback) setUseFallback(true);
          else setBroken(true);
        }}
        data-testid="milestone-video-clip"
      />
      <div className={`absolute inset-0 ring-1 ${ringClass} pointer-events-none`} />
    </div>
  );
}

function TrophyFallback({ color, Icon }) {
  return (
    <motion.div
      initial={{ scale: 0.7, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 14 }}
      className={`aspect-video rounded-lg border flex items-center justify-center ${
        color === 'amber'
          ? 'border-amber-500/30 bg-gradient-to-br from-amber-700/30 to-amber-900/20'
          : color === 'sky'
            ? 'border-sky-500/30 bg-gradient-to-br from-sky-700/30 to-sky-900/20'
            : color === 'emerald'
              ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-700/30 to-emerald-900/20'
              : 'border-slate-500/30 bg-gradient-to-br from-slate-700/30 to-slate-900/20'
      }`}
      data-testid="milestone-video-fallback"
    >
      <Icon className={`w-16 h-16 ${
        color === 'amber'
          ? 'text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,0.7)]'
          : color === 'sky' ? 'text-sky-300'
            : color === 'emerald' ? 'text-emerald-300'
              : 'text-slate-500'
      }`} />
    </motion.div>
  );
}
